import { Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import { integrationService } from "/app/src/services";
import { useCallback, useState } from "react";
import { Button, Textarea } from "@heroui/react";
import Box from "/app/src/components/generic/components/box";
import { isJSON } from "/app/src/helpers";
import Summary from "../common/Summary";

/**
 * Component to display the manual run component for a single Data Pull Integration
 */
export default function Run({ integration }: { integration: Integration }) {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [textValue, setTextValue] = useState<string>("");
  const [textError, setTextError] = useState<string | null>(null);

  /**
   * Test the integration
   */
  const handleRunClick = useCallback(async () => {
    setIsRunning(true);
    setResult(null);
    setTextError(null);
    let body = {};
    if (!isJSON(textValue)) {
      setTextError("Invalid JSON");
      setIsRunning(false);
      return;
    }
    body = JSON.parse(textValue);

    const response = await integrationService.runIntegration(
      integration.id,
      body,
    );
    // New backend shape: { message, summary }
    setResult(response);
    setIsRunning(false);
  }, [integration.id, textValue]);

  const handleTextChange = useCallback(
    (e) => {
      const val = e.target.value;
      setTextValue(val);
      if (textError) {
        if (isJSON(val)) setTextError(null);
      }
    },
    [textError],
  );

  return (
    <Box>
      <Textarea
        className="mt-4 mb-4"
        value={textValue}
        onChange={handleTextChange}
        maxRows={20}
        isInvalid={Boolean(textError)}
        errorMessage={textError || undefined}
      />
      <Row gutter={16}>
        <Col span={12}>
          {t("translation:manual_run_tip")}
          <br />
          {`/api/integrations/${integration.id}/run`}
        </Col>
        <Col offset={4} span={4} />
        <Col span={4}>
          <Button
            color="primary"
            radius="none"
            fullWidth
            onPress={handleRunClick}
            isDisabled={isRunning}
            className="mt-4"
          >
            {t("translation:run")}
          </Button>
        </Col>
      </Row>
      {result && <Summary result={result} title="Run Summary" />}
    </Box>
  );
}
